import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from '@mui/material';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { IPrivateSessionAppointment } from '../../../types';

/**
 * GH-666 — move a private session to a new date/time from the admin panel.
 * Unlike the member/expert flow this is not bound by the cancellation window,
 * the payment confirmation or the booking quotas.
 */
const EditSessionDialog = ({
	open,
	sessionToEdit,
	onClose,
	handleRescheduleSession,
}: {
	open: boolean;
	sessionToEdit: IPrivateSessionAppointment | null;
	onClose: () => void;
	handleRescheduleSession: (id: string, newTime: string, silent?: boolean) => Promise<void>;
}) => {
	const [newTime, setNewTime] = useState('');
	// The reschedule takes a few seconds server-side (emails, reminders, calendar), so the
	// dialog has to stay put and refuse a second click until it comes back.
	const [submitting, setSubmitting] = useState<'normal' | 'silent' | null>(null);

	// Reset to the session's current time every time the dialog opens on another row.
	useEffect(() => {
		if (sessionToEdit?.time) setNewTime(moment(sessionToEdit.time).format('YYYY-MM-DDTHH:mm'));
		setSubmitting(null);
	}, [sessionToEdit]);

	const unchanged = !newTime || moment(newTime).isSame(moment(sessionToEdit?.time), 'minute');
	// A past target time is allowed for admins (tidying up a session that already happened),
	// but it is worth saying out loud what the notifying button would do with it.
	const inPast = Boolean(newTime) && moment(newTime).isBefore(moment());
	// Moving an appointment whose original time already passed is allowed for admins,
	// but it is worth flagging so an old session is not rescheduled by mistake.
	const sessionAlreadyPassed = Boolean(sessionToEdit?.time) && moment(sessionToEdit?.time).isBefore(moment());

	const submit = async (silent?: boolean) => {
		if (!sessionToEdit?._id || !newTime || submitting) return;
		setSubmitting(silent ? 'silent' : 'normal');
		try {
			await handleRescheduleSession(sessionToEdit._id, moment(newTime).toISOString(), silent);
		} finally {
			setSubmitting(null);
		}
	};

	return (
		<Dialog open={open} onClose={() => !submitting && onClose()} fullWidth maxWidth="xs">
			<DialogTitle>עריכת מועד פגישה</DialogTitle>
			<DialogContent>
				<Typography variant="body2" whiteSpace={'pre-line'} textAlign={'center'} marginBottom={sessionAlreadyPassed ? 1 : 2}>
					{`המועד הנוכחי: ${moment(sessionToEdit?.time).format('DD/MM HH:mm')}\n${sessionToEdit?.email ?? ''}`}
				</Typography>
				{sessionAlreadyPassed && (
					<Alert severity="warning" sx={{ marginBottom: 2 }}>
						הפגישה הזו כבר עברה — ודאי שזו הפגישה שרצית להזיז
					</Alert>
				)}
				<TextField
					type="datetime-local"
					label="מועד חדש"
					value={newTime}
					onChange={(e) => setNewTime(e.target.value)}
					fullWidth
					InputLabelProps={{ shrink: true }}
				/>
				{inPast && (
					<Typography variant="body2" color="warning.main" marginTop={1} textAlign={'center'}>
						המועד שנבחר כבר עבר — עדכון רגיל ישלח לשני הצדדים הודעה על מפגש שכבר היה
					</Typography>
				)}
			</DialogContent>
			<DialogActions>
				<Box display={'flex'} width={'100%'} justifyContent={'space-between'} alignItems={'center'}>
					<Button onClick={onClose} disabled={Boolean(submitting)}>
						ביטול
					</Button>
					<Button
						color="primary"
						disabled={unchanged || Boolean(submitting)}
						onClick={() => submit()}
						startIcon={submitting === 'normal' ? <CircularProgress size={14} color="inherit" /> : undefined}
					>
						{submitting === 'normal' ? 'מעדכן…' : 'עדכון'}
					</Button>
					<Button
						color="primary"
						disabled={unchanged || Boolean(submitting)}
						onClick={() => submit(true)}
						startIcon={submitting === 'silent' ? <CircularProgress size={14} color="inherit" /> : undefined}
						style={{ display: 'flex', flexDirection: 'column' }}
					>
						<Typography variant="body2" display={'block'}>
							{submitting === 'silent' ? 'מעדכן…' : 'עדכון שקט'}
						</Typography>
						<Typography variant="body2" display={'block'} fontSize={10}>
							ללא מייל, וואטסאפ ויומן
						</Typography>
					</Button>
				</Box>
			</DialogActions>
		</Dialog>
	);
};

export default EditSessionDialog;
